import { motion } from 'framer-motion';
import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useRegion } from '../contexts/RegionContext';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import UserMenu from './UserMenu';
import { AuthModal } from './AuthModal';
import { supabase } from '../lib/supabase';
import { aparatApi } from '../lib/aparatApi';
import { HiMenu, HiX } from 'react-icons/hi';
import React from 'react'; // Added for React.Children.map
import LanguageToggle from './LanguageToggle';

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [authMode, setAuthMode] = useState<'login' | 'signup'>('login');
  const { user } = useAuth();
  const { t, direction } = useRegion();
  const location = useLocation();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const searchTimeout = useRef<NodeJS.Timeout | null>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleAuthClick = (mode: 'login' | 'signup') => {
    setAuthMode(mode);
    setIsAuthModalOpen(true);
  };

  const handleGalleryClick = (e: React.MouseEvent | React.KeyboardEvent) => {
    e.preventDefault?.();
    
    if (location.pathname !== '/') {
      navigate('/');
      setTimeout(() => {
        const galleryElement = document.getElementById('gallery');
        if (galleryElement) {
          const headerOffset = 80;
          const elementPosition = galleryElement.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          });
        }
      }, 100);
    } else {
      const galleryElement = document.getElementById('gallery');
      if (galleryElement) {
        const headerOffset = 80;
        const elementPosition = galleryElement.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
      }
    }
  };

  const handleNavClick = (e: React.MouseEvent | React.KeyboardEvent, targetId: string) => {
    e.preventDefault?.();
    if (location.pathname !== '/') {
      navigate('/');
      setTimeout(() => {
        const targetElement = document.getElementById(targetId);
        if (targetElement) {
          const headerOffset = 80;
          const elementPosition = targetElement.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          });
        }
      }, 100);
    } else {
      const targetElement = document.getElementById(targetId);
      if (targetElement) {
        const headerOffset = 80;
        const elementPosition = targetElement.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
      }
    }
  };

  const handleSearch = async (query: string) => {
    if (!query.trim()) {
      setSearchResults([]);
      setShowDropdown(false);
      return;
    }
    setSearchLoading(true);
    setSearchError(null);
    setShowDropdown(true);
    try {
      // Search games by title/description
      const { data: games, error: gamesError } = await supabase
        .from('games')
        .select('*')
        .or(`title.ilike.%${query}%,description.ilike.%${query}%`)
        .eq('is_visible', true)
        .limit(20);
      if (gamesError) throw gamesError;
      // Search all games for content_blocks
      const { data: allGames, error: allGamesError } = await supabase
        .from('games')
        .select('*')
        .eq('is_visible', true);
      if (allGamesError) throw allGamesError;
      const lowerQuery = query.toLowerCase();
      const contentBlockMatches = (allGames || []).filter(g =>
        Array.isArray(g.content_blocks) && g.content_blocks.some((block: any) =>
          (block.content && block.content.toLowerCase().includes(lowerQuery)) ||
          (block.caption && block.caption.toLowerCase().includes(lowerQuery))
        )
      );
      // Merge and deduplicate
      const allGameResults = [...(games || [])];
      contentBlockMatches.forEach(match => {
        if (!allGameResults.find(g => g.id === match.id)) {
          allGameResults.push(match);
        }
      });
      // Limit to 10 results
      const finalGames = allGameResults.slice(0, 10);
      // Search videos (Aparat)
      let videos: any[] = [];
      try {
        const aparatRes = await aparatApi.searchVideos(query, 1, 5);
        if (Array.isArray(aparatRes)) {
          videos = aparatRes;
        } else if (Array.isArray((aparatRes as any)?.included)) {
          videos = (aparatRes as any).included;
        } else if (Array.isArray((aparatRes as any)?.data)) {
          videos = (aparatRes as any).data;
        } else {
          videos = [];
        }
      } catch (e) {
        // ignore aparat error
      }
      setSearchResults([
        { type: 'games', items: finalGames },
        { type: 'videos', items: videos }
      ]);
    } catch (err: any) {
      setSearchError('خطا در جستجو.');
    } finally {
      setSearchLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => {
      handleSearch(e.target.value);
    }, 500);
  };

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    handleSearch(search);
  };

  const renderNavLinks = () => {
    if (location.pathname === '/') {
      return (
        <>
          <Link to="/about" className="nav-link">{t('nav.about')}</Link>
          <Link to="/games" className="nav-link">{t('nav.games')}</Link>
          <Link to="/blog" className="nav-link">{t('nav.blog')}</Link>
          {user && <Link to="/my-games" className="nav-link">{t('nav.myGames')}</Link>}
          <span
            role="button"
            tabIndex={0}
            onClick={(e) => { if (mobileMenuOpen) setMobileMenuOpen(false); setTimeout(() => handleGalleryClick(e), 100); }}
            onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { if (mobileMenuOpen) setMobileMenuOpen(false); setTimeout(() => handleGalleryClick(e), 100); } }}
            className="nav-link cursor-pointer"
          >
            {t('nav.gallery')}
          </span>
          <Link to="/contact" className="nav-link">{t('nav.contact')}</Link>
        </>
      );
    }
    return (
      <>
        <Link to="/" className="nav-link">{t('nav.home')}</Link>
        <Link to="/games" className="nav-link">{t('nav.games')}</Link>
        <Link to="/blog" className="nav-link">{t('nav.blog')}</Link>
        {user && <Link to="/my-games" className="nav-link">{t('nav.myGames')}</Link>}
        <span
          role="button"
          tabIndex={0}
          onClick={handleGalleryClick}
          onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') handleGalleryClick(e); }}
          className="nav-link cursor-pointer"
        >
          {t('nav.gallery')}
        </span>
        <Link to="/contact" className="nav-link">{t('nav.contact')}</Link>
      </>
    );
  };

  return (
    <>
      <motion.nav
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        className={`fixed top-0 right-0 left-0 z-50 transition-all duration-300 ${
          isScrolled
            ? 'bg-black/95 backdrop-blur-2xl py-2.5 sm:py-3 border-b border-white/15 shadow-2xl shadow-black/80'
            : 'bg-black/90 backdrop-blur-xl py-3.5 sm:py-4 border-b border-white/10'
        }`}
        style={{
          willChange: 'transform',
          transform: 'translateZ(0)',
          backfaceVisibility: 'hidden',
          perspective: '1000px'
        }}
      >
        <div className="container mx-auto px-4 flex items-center justify-between gap-4">
          <Link to="/" className="shrink-0 flex items-center">
            <motion.img
              src={import.meta.env.BASE_URL + 'AE logo.svg'}
              alt="Active Legend"
              className="h-8 sm:h-9 w-auto"
              whileHover={{ scale: 1.05 }}
            />
          </Link>
          {/* Desktop Search Bar */}
          <form onSubmit={handleSearchSubmit} className="relative flex-1 max-w-sm lg:max-w-md mx-3 hidden md:block">
            <input
              type="text"
              className={`w-full bg-zinc-900/90 border border-white/15 rounded-xl py-2 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all text-xs sm:text-sm ${
                direction === 'rtl' ? 'pr-9 pl-4 text-right' : 'pl-9 pr-4 text-left'
              }`}
              placeholder={t('nav.searchPlaceholder')}
              value={search}
              onChange={handleInputChange}
              onFocus={() => search && setShowDropdown(true)}
              onBlur={() => setTimeout(() => setShowDropdown(false), 200)}
              dir={direction}
            />
            <button
              type="submit"
              className={`absolute top-1/2 -translate-y-1/2 text-primary hover:text-white transition-colors ${
                direction === 'rtl' ? 'right-3' : 'left-3'
              }`}
            >
              <svg width="18" height="18" fill="none" viewBox="0 0 24 24">
                <path stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35m1.35-5.15a7 7 0 11-14 0 7 7 0 0114 0z"/>
              </svg>
            </button>
            {showDropdown && (
              <div className="absolute right-0 left-0 mt-2 bg-black/95 border border-white/10 rounded-xl shadow-lg z-50 max-h-80 overflow-y-auto">
                {searchLoading && <div className="p-4 text-center text-gray-400">{t('search.loading')}</div>}
                {searchError && <div className="p-4 text-center text-red-400">{searchError}</div>}
                {!searchLoading && !searchError && searchResults.length === 0 && (
                  <div className="p-4 text-center text-gray-400">{t('search.noResults')}</div>
                )}
                {!searchLoading && !searchError && searchResults.map((group, idx) => (
                  <div key={group.type} className="border-b border-white/5 last:border-b-0">
                    <div className="px-4 py-2 text-xs text-primary font-bold">
                      {group.type === 'games' ? t('search.games') : group.type === 'videos' ? t('search.videos') : ''}
                    </div>
                    {group.items.length === 0 ? (
                      <div className="px-4 py-2 text-gray-500 text-xs">{t('search.noneInCategory')}</div>
                    ) : (
                      group.items.map((item: any) => (
                    <Link
                          key={item.id || item.uid || item.slug}
                          to={group.type === 'games' ? `/games/${item.slug}` : item.url || item.link || '#'}
                          target={group.type === 'videos' ? '_blank' : undefined}
                          className="block px-4 py-2 hover:bg-primary/10 text-sm text-white transition-colors rounded-lg"
                          onClick={() => setShowDropdown(false)}
                        >
                          {group.type === 'games' ? (
                            <>
                              <span className="font-bold">{item.title}</span>
                              <span className="text-xs text-gray-400 ml-2">{item.description?.slice(0, 40)}...</span>
                            </>
                          ) : (
                            <>
                              <span className="font-bold">{item.title || item.name}</span>
                              <span className="text-xs text-gray-400 ml-2">{item.description?.slice(0, 40) || ''}</span>
                            </>
                          )}
                    </Link>
                      ))
                    )}
                  </div>
                ))}
              </div>
            )}
          </form>
          {/* Hamburger Icon, LanguageToggle and UserMenu for Mobile */}
          <div className="flex items-center gap-2 lg:hidden">
            <LanguageToggle showLabel={false} />
            <button
              className="flex items-center justify-center p-2 rounded-md text-white hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-primary order-2"
              onClick={() => setMobileMenuOpen(true)}
              aria-label={t('nav.openMenu')}
              type="button"
            >
              <HiMenu className="w-8 h-8" />
            </button>
            {user && <div className="order-1"><UserMenu /></div>}
          </div>
          {/* Desktop Nav/User */}
          <div className="hidden lg:flex items-center gap-6">
            {renderNavLinks()}
            <LanguageToggle />
            {user ? (
              <UserMenu />
            ) : (
              <div className="flex items-center gap-3">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="btn-secondary text-xs px-4 py-2"
                  onClick={() => handleAuthClick('login')}
                >
                  {t('nav.login')}
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="btn-primary text-xs px-4 py-2"
                  onClick={() => handleAuthClick('signup')}
                >
                  {t('nav.signup')}
                </motion.button>
              </div>
            )}
          </div>
        </div>
        {/* Mobile Menu Drawer */}
        {mobileMenuOpen && (
          <div className="fixed inset-0 z-[999] bg-black flex flex-col lg:hidden">
            <div className="flex items-center justify-between px-4 py-4 border-b border-white/10">
              <Link to="/" onClick={() => setMobileMenuOpen(false)}>
                <img src={import.meta.env.BASE_URL + 'AE logo.svg'} alt="Active Legend" className="h-8 w-auto" />
              </Link>
              <div className="flex items-center gap-3">
                <LanguageToggle />
                <button
                  className="p-2 rounded-md text-white hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-primary"
                  onClick={() => setMobileMenuOpen(false)}
                  aria-label={t('nav.closeMenu')}
                  type="button"
                >
                  <HiX className="w-8 h-8" />
                </button>
              </div>
            </div>
            {/* Mobile Search Bar */}
            <form onSubmit={handleSearchSubmit} className="relative p-4 border-b border-white/10">
              <input
                type="text"
                className="w-full bg-black/60 border border-white/10 rounded-xl py-3 px-4 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary transition-all"
                placeholder={t('nav.searchPlaceholder')}
                value={search}
                onChange={handleInputChange}
                onFocus={() => search && setShowDropdown(true)}
                onBlur={() => setTimeout(() => setShowDropdown(false), 200)}
                dir={direction}
              />
              <button type="submit" className="absolute left-6 top-1/2 -translate-y-1/2 text-primary hover:text-white transition-colors">
                <svg width="22" height="22" fill="none" viewBox="0 0 24 24"><path stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35m1.35-5.15a7 7 0 11-14 0 7 7 0 0114 0z"/></svg>
              </button>
              {showDropdown && (
                <div className="absolute right-0 left-0 mt-2 bg-black/95 border border-white/10 rounded-xl shadow-lg z-50 max-h-80 overflow-y-auto">
                  {searchLoading && <div className="p-4 text-center text-gray-400">{t('search.loading')}</div>}
                  {searchError && <div className="p-4 text-center text-red-400">{searchError}</div>}
                  {!searchLoading && !searchError && searchResults.length === 0 && (
                    <div className="p-4 text-center text-gray-400">{t('search.noResults')}</div>
                  )}
                  {!searchLoading && !searchError && searchResults.map((group, idx) => (
                    <div key={group.type} className="border-b border-white/5 last:border-b-0">
                      <div className="px-4 py-2 text-xs text-primary font-bold">
                        {group.type === 'games' ? t('search.games') : group.type === 'videos' ? t('search.videos') : ''}
                      </div>
                      {group.items.length === 0 ? (
                        <div className="px-4 py-2 text-gray-500 text-xs">{t('search.noneInCategory')}</div>
                      ) : (
                        group.items.map((item: any) => (
                          <Link
                            key={item.id || item.uid || item.slug}
                            to={group.type === 'games' ? `/games/${item.slug}` : item.url || item.link || '#'}
                            target={group.type === 'videos' ? '_blank' : undefined}
                            className="block px-4 py-2 hover:bg-primary/10 text-sm text-white transition-colors rounded-lg bg-black"
                            onClick={() => setMobileMenuOpen(false)}
                          >
                            {group.type === 'games' ? (
                              <>
                                <span className="font-bold">{item.title}</span>
                                <span className="text-xs text-gray-400 ml-2">{item.description?.slice(0, 40)}...</span>
                              </>
              ) : (
                              <>
                                <span className="font-bold">{item.title || item.name}</span>
                                <span className="text-xs text-gray-400 ml-2">{item.description?.slice(0, 40) || ''}</span>
                              </>
                            )}
                          </Link>
                        ))
                      )}
                    </div>
                  ))}
                </div>
              )}
            </form>
            {/* Mobile Nav & Auth Section with Shared Background */}
            <div className="mx-4 my-6 rounded-2xl bg-black/80 p-2 flex flex-col gap-1">
              <nav className="flex flex-col gap-1">
                {renderNavLinks() &&
                  React.Children.map(renderNavLinks().props.children, (child) =>
                    child && React.cloneElement(child, {
                      className: (child.props.className || '') + ' rounded-lg px-3 py-2 hover:bg-white/10 transition-colors',
                      onClick: () => setMobileMenuOpen(false),
                    })
                  )
                }
              </nav>
              <div className="flex flex-col gap-1 mt-2">
                {user ? null : (
                  <>
                    <button
                      className="btn-secondary w-full rounded-lg mb-1"
                      onClick={() => { setMobileMenuOpen(false); handleAuthClick('login'); }}
                    >
                      {t('nav.login')}
                    </button>
                    <button
                      className="btn-primary w-full rounded-lg"
                      onClick={() => { setMobileMenuOpen(false); handleAuthClick('signup'); }}
                    >
                      {t('nav.signup')}
                    </button>
                  </>
                )}
        </div>
            </div>
          </div>
        )}
      </motion.nav>
        <AuthModal
          isOpen={isAuthModalOpen}
          onClose={() => setIsAuthModalOpen(false)}
        initialMode={authMode}
        />
    </>
  );
}